import React, { useEffect, useState } from "react";
import { Skeleton } from "antd";
import {
  FaUsers,
  FaChartLine,
  FaMoneyBillWave,
  FaFutbol,
} from "react-icons/fa";
import DashboardService from "../../services/dashboardService";
import { listGrupos } from "../../services/gruposService";
import { listEventosActivosFechasTipos } from "../../services/eventosService";
// convocatorias service removed (not needed for new chart)
import { Select } from "antd";
import {
  PieChart,
  Pie,
  Cell,
  Sector,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  // AreaChart and Area removed (not used)
} from "recharts";
import useAuth from "../../hooks/useAuth";

const KPI_PLACEHOLDERS = [
  { key: "totalUsers", label: "Usuarios", icon: FaUsers },
  {
    key: "asistenciaPromedio",
    label: "Asistencia promedio",
    icon: FaChartLine,
  },
  // Unificar recaudado mensual y anual en una sola card
  { key: "recaudado", label: "Recaudado", icon: FaMoneyBillWave },
  // Mostrar total de deportistas en la última card
  { key: "totalDeportistas", label: "Deportistas", icon: FaFutbol },
];

const PALETTE = [
  "#4f46e5",
  "#06b6d4",
  "#f59e0b",
  "#ef4444",
  "#10b981",
  "#8b5cf6",
];

function fmtNumber(v) {
  if (v === undefined || v === null) return "--";
  return String(v);
}

function fmtCurrency(v) {
  if (v === undefined || v === null) return "--";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(Number(v));
  } catch {
    return String(v);
  }
}

export default function Dashboard() {
  const { lettersTitlesColor, lettersColor, linesColor } = useAuth();

  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);

  // Chart states
  const [grupos, setGrupos] = useState([]);
  const [deportistasDist, setDeportistasDist] = useState([]);
  const [generoFilter, setGeneroFilter] = useState(null);

  const [eventos, setEventos] = useState([]);
  // Asistencias card (independiente)
  const [selectedEventoAsistencias, setSelectedEventoAsistencias] =
    useState(null);
  const [asistenciasData, setAsistenciasData] = useState([]);

  // Deportistas por grupos (nuevo grafico Pie)
  const [deportistasByGrupos, setDeportistasByGrupos] = useState([]);
  const [deportistasByGruposLoading, setDeportistasByGruposLoading] =
    useState(true);
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const [selectedGrupoByGrupos, setSelectedGrupoByGrupos] = useState(null);

  // (Tarjetas chart replaced by Deportistas por Grupo pie)

  const [tipoEvento, setTipoEvento] = useState(null);
  const [proximosData, setProximosData] = useState([]);
  const [proximosLoading, setProximosLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    (async function load() {
      try {
        setLoading(true);
        const data = await DashboardService.getKpis();
        // service returns the inner `data` object ({ totalUsers, ... })
        if (!mounted) return;
        setKpis(data || {});
      } catch (err) {
        console.error("Error cargando KPIs:", err);
        setKpis({});
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    // load auxiliary lists for charts
    (async function loadLists() {
      try {
        const [g, ev] = await Promise.all([
          listGrupos(),
          listEventosActivosFechasTipos(),
        ]);
        if (!mounted) return;
        setGrupos(g || []);
        setEventos(ev || []);
      } catch (err) {
        console.error("Error cargando listas para charts:", err);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // When generoFilter changes, fetch distribution by position
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const filters = {};
        if (generoFilter) filters.genero = generoFilter;
        
        const resp = await DashboardService.getDeportistasDistribution(filters);
        // resp.data expected array -> map to { name, value }
        const raw = resp?.data ?? [];
        const mapped = (Array.isArray(raw) ? raw : []).map((r) => ({
          name: r.posicion_nombre || r.nombre || r.name || String(r.posicion_id || "-"),
          value: Number(r.total_deportistas ?? r.cantidad ?? r.value ?? r.count ?? r.total ?? 0),
        }));
        if (mounted) setDeportistasDist(mapped);
      } catch (err) {
        console.error("Error cargando distribución:", err);
        if (mounted) setDeportistasDist([]);
      }
    })();
    return () => (mounted = false);
  }, [generoFilter]);

  // When selectedEventoAsistencias changes, fetch asistencias
  useEffect(() => {
    let mounted = true;
    (async () => {
      // If selectedEventoAsistencias is null/undefined -> load unfiltered data (Todos)
      const shouldFilter =
        selectedEventoAsistencias !== null &&
        selectedEventoAsistencias !== undefined;
      try {
        // Use the new endpoint that returns asistencias por grupo
        const resp = await DashboardService.getAsistenciasPorGrupos(
          shouldFilter ? { evento_id: selectedEventoAsistencias } : {}
        );
        const items = Array.isArray(resp) ? resp : resp?.data ?? [];
        const mapped = (Array.isArray(items) ? items : []).map((r) => ({
          // label for the group: prefer 'categoria', fallback to 'grupo_nombre'
          name:
            r.categoria || r.grupo_nombre || String(r.grupo_id || r.id || "-"),
          presentes: Number(r.presentes ?? r.presente ?? r.present ?? 0),
          ausentes: Number(r.ausentes ?? r.ausencia ?? r.absent ?? 0),
          tardanzas: Number(r.tardanzas ?? r.tardanza ?? r.late ?? 0),
          justificados: Number(
            r.justificados ?? r.justificado ?? r.excused ?? 0
          ),
        }));
        if (mounted) setAsistenciasData(mapped);
      } catch (err) {
        console.error("Error cargando asistencias por grupo:", err);
        if (mounted) setAsistenciasData([]);
      }
    })();
    return () => (mounted = false);
  }, [selectedEventoAsistencias]);

  // Load deportistas by grupos for the new pie chart
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (mounted) setDeportistasByGruposLoading(true);
        const filters = selectedGrupoByGrupos
          ? { grupo: selectedGrupoByGrupos }
          : {};
        const data = await DashboardService.getDeportistasByGrupos(filters);
        const items = Array.isArray(data) ? data : data?.data ?? [];
        const mapped = (Array.isArray(items) ? items : []).map((it) => ({
          id: it.grupo_id || it.grupo || Math.random().toString(36).slice(2, 9),
          name:
            it.categoria ||
            it.categoria_nombre ||
            it.categoria ||
            it.label ||
            "-",
          value: Number(it.total_deportistas ?? it.total ?? it.count ?? 0),
        }));
        if (mounted) setDeportistasByGrupos(mapped);
      } catch (err) {
        console.error("Error cargando deportistas por grupos:", err);
        if (mounted) setDeportistasByGrupos([]);
      } finally {
        if (mounted) setDeportistasByGruposLoading(false);
      }
    })();
    return () => (mounted = false);
  }, [selectedGrupoByGrupos]);

  // custom active shape for Pie (connector + PV label, no rate)
  const renderActiveShape = (props) => {
    const {
      cx,
      cy,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
    } = props;

    return (
      <g>
        <text
          x={cx}
          y={cy}
          dy={8}
          textAnchor="middle"
          fill={fill}
          style={{ fontWeight: 600 }}
        >
          {payload?.name}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={(outerRadius ?? 0) + 6}
          outerRadius={(outerRadius ?? 0) + 10}
          fill={fill}
        />
      </g>
    );
  };

  // proximos by tipo (load list of upcoming events, show skeleton while loading)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (mounted) setProximosLoading(true);
        const resp = await DashboardService.getProximosEventos({
          tipo: tipoEvento,
        });
        const raw = resp ?? [];
        const items = Array.isArray(raw) ? raw : raw?.data ?? [];
        // map items to table rows
        const mapped = (Array.isArray(items) ? items : [])
          .map((it) => ({
            id:
              it.id ||
              it.evento_id ||
              it._id ||
              Math.random().toString(36).slice(2, 9),
            date:
              (it.fecha_inicio || it.date || it.fecha || "")
                .slice(0, 16)
                .replace("T", " ") || "-",
            name: it.nombre || it.title || it.name || it.evento_nombre || "-",
            tipo: it.tipo || it.type || "-",
            lugar: it.lugar || it.location || it.place || "-",
          }))
          .sort((a, b) => ((a.date || "") > (b.date || "") ? 1 : -1));
        if (mounted) setProximosData(mapped);
      } catch (err) {
        console.error("Error cargando proximos:", err);
        if (mounted) setProximosData([]);
      } finally {
        if (mounted) setProximosLoading(false);
      }
    })();
    return () => (mounted = false);
  }, [tipoEvento]);

  return (
    <div className="px-10 py-5 max-w-[95vw] mx-auto items-start min-h-[60vh] relative">
      <div className="mt-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {KPI_PLACEHOLDERS.map((kp) => {
            const Icon = kp.icon;
            const raw = kpis ? kpis[kp.key] : undefined;
            const isRecaudado = kp.key === "recaudado";
            const value = fmtNumber(raw);

            return (
              <div
                key={kp.key}
                className={`rounded-xl border ${linesColor} p-6 min-h-[120px] flex flex-col justify-center gap-2 bg-white`}
              >
                {loading ? (
                  <div className="flex items-center justify-between">
                    <div className="flex-1 pr-4">
                      <Skeleton active title={false} paragraph={{ rows: 1 }} />
                    </div>
                    <div className="w-10 h-10 rounded-full bg-gray-200 shrink-0 animate-pulse" />
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between">
                      <div className={`text-sm ${lettersColor}`}>
                        {kp.label}
                      </div>
                      <div className="text-xl text-gray-500">
                        <Icon />
                      </div>
                    </div>

                    {isRecaudado ? (
                      <div>
                        <div className={`${lettersTitlesColor} text-2xl font-bold`}>
                          {fmtCurrency(kpis?.recaudadoMensual)}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Anual: {fmtCurrency(kpis?.recaudadoAnual)}</div>
                      </div>
                    ) : (
                      <div className={`${lettersTitlesColor} text-2xl font-bold`}>
                        {kp.key === "totalDeportistas" 
                          ? fmtNumber(kpis?.totalDeportistas) 
                          : kp.key === "asistenciaPromedio" 
                          ? `${value}%` 
                          : value}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Charts grid (2x2) */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chart 1: Distribución de Deportistas por Posición (Pie with gap) */}
        <div className={`rounded-xl border ${linesColor} p-4 bg-white`}>
          <div className="flex items-center justify-between mb-3">
            <div className={`${lettersTitlesColor} font-semibold`}>
              Distribución por Posición
            </div>
            <Select
              placeholder="Género"
              style={{ width: 160 }}
              allowClear
              value={generoFilter}
              onChange={(val) => setGeneroFilter(val)}
            >
              <Select.Option value={undefined}>Todo</Select.Option>
              <Select.Option value="Masculino">Masculino</Select.Option>
              <Select.Option value="Femenino">Femenino</Select.Option>
            </Select>
          </div>
          <div style={{ width: "100%", height: 340 }}>
            {deportistasDist.length === 0 ? (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-gray-500">
                  No hay datos de distribución por posición
                </div>
              </div>
            ) : (
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={deportistasDist}
                    cx="50%"
                    cy="45%"
                    innerRadius={60}
                    outerRadius={95}
                    paddingAngle={5}
                    dataKey="value"
                    cornerRadius={8}
                  >
                    {deportistasDist.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={PALETTE[index % PALETTE.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload || !payload.length) return null;
                      const data = payload[0];
                      return (
                        <div
                          className="bg-gray-900 px-3 py-2 rounded shadow-md"
                          style={{ minWidth: 120 }}
                        >
                          <div className="text-white text-sm font-medium mb-1">
                            {data.name}
                          </div>
                          <div className="text-white text-xs opacity-90">
                            {data.value} deportistas
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={80}
                    iconType="circle"
                    wrapperStyle={{
                      paddingTop: 10,
                      fontSize: "12px",
                      maxHeight: 80,
                      overflowY: "auto",
                    }}
                    layout="horizontal"
                    align="center"
                    formatter={(value) => (
                      <span className="text-xs text-gray-700" style={{ fontSize: "11px" }}>
                        {value}
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Chart 2: Asistencias por evento (Stacked Bar) */}
        <div
          className={`rounded-xl border ${linesColor} pr-3 pl-3 py-4 bg-white`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className={`${lettersTitlesColor} font-semibold`}>
              Asistencias por Grupo
            </div>
            <Select
              placeholder="Filtrar por Evento"
              style={{ width: 240 }}
              allowClear
              value={selectedEventoAsistencias}
              onChange={(val) => setSelectedEventoAsistencias(val)}
            >
              {/* Default 'Todos' option - value null means no filtro */}
              <Select.Option key="all" value={null}>
                Todos
              </Select.Option>
              {eventos.map((e) => (
                <Select.Option
                  key={e.id || e}
                  value={e.id || e.value || e.fecha_inicio || e.nombre}
                >
                  {e.nombre || e.name || e.fecha_inicio || e.tipo}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div
            style={{
              width: "100%",
              height: Math.max(280, asistenciasData.length * 60 + 80),
            }}
          >
            {asistenciasData.length === 0 ? (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-gray-500">
                  No hay datos de asistencias por grupo
                </div>
              </div>
            ) : (
              <ResponsiveContainer>
                {/* Horizontal stacked bars: layout="vertical" makes bars horizontal (X = counts, Y = category names) */}
                <BarChart
                  data={asistenciasData}
                  layout="vertical"
                  // Adjusted margins: more left margin for labels, balanced right margin
                  margin={{ top: 40, right: 30, left: 10, bottom: 10 }}
                  barCategoryGap={8}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  {/* Increased width for category labels and better positioning */}
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={80}
                    tick={{ fill: "#334155", fontSize: 13 }}
                  />
                  {/* Custom tooltip to show counts per state */}
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload || !payload.length) return null;
                      return (
                        <div
                          className="bg-white p-2 rounded shadow-sm border"
                          style={{ minWidth: 140 }}
                        >
                          <div className="font-semibold text-sm mb-1">
                            {label}
                          </div>
                          {payload
                            .filter((p) => p.value !== undefined)
                            .map((p) => (
                              <div
                                key={p.dataKey}
                                className="flex items-center gap-2 text-sm"
                                style={{ color: p.color }}
                              >
                                <div
                                  style={{
                                    width: 10,
                                    height: 10,
                                    background: p.color,
                                    borderRadius: 2,
                                  }}
                                />
                                <div style={{ minWidth: 90 }}>{p.name}</div>
                                <div className="font-medium">{p.value}</div>
                              </div>
                            ))}
                        </div>
                      );
                    }}
                  />
                  {/* Center the legend above the chart */}
                  <Legend
                    verticalAlign="top"
                    align="center"
                    wrapperStyle={{ paddingBottom: 4 }}
                  />
                  {/* Bars: order chosen for visual clarity */}
                  <Bar
                    dataKey="presentes"
                    stackId="a"
                    fill="#4f46e5"
                    name="Presentes"
                    barSize={20}
                  />
                  <Bar
                    dataKey="justificados"
                    stackId="a"
                    fill="#10b981"
                    name="Justificados"
                    barSize={20}
                  />
                  <Bar
                    dataKey="tardanzas"
                    stackId="a"
                    fill="#f59e0b"
                    name="Tardanzas"
                    barSize={20}
                  />
                  <Bar
                    dataKey="ausentes"
                    stackId="a"
                    fill="#ef4444"
                    name="Ausentes"
                    barSize={20}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Chart 3: Deportistas por Grupo (Pie - custom active shape) */}
        <div className={`rounded-xl border ${linesColor} p-4 bg-white`}>
          <div className="flex items-center justify-between mb-3">
            <div className={`${lettersTitlesColor} font-semibold`}>
              Deportistas por Grupo
            </div>
            <Select
              placeholder="Filtrar grupo"
              style={{ width: 200 }}
              allowClear
              value={selectedGrupoByGrupos}
              onChange={(val) => setSelectedGrupoByGrupos(val)}
            >
              <Select.Option value={null}>Todos</Select.Option>
              {grupos.map((g) => (
                <Select.Option
                  key={g.id || g.grupo_id || g.categoria}
                  value={g.id || g.grupo_id || g.id}
                >
                  {g.categoria || g.nombre || g.name || g.label}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div style={{ width: "100%", height: 260 }}>
            {deportistasByGruposLoading ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : (
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    activeIndex={activeGroupIndex}
                    activeShape={renderActiveShape}
                    data={deportistasByGrupos}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={120}
                    dataKey="value"
                    nameKey="name"
                    onMouseEnter={(_, idx) => setActiveGroupIndex(idx)}
                  >
                    {deportistasByGrupos.map((entry, idx) => (
                      <Cell
                        key={`cell-gr-${idx}`}
                        fill={PALETTE[idx % PALETTE.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload || !payload.length) return null;
                      const data = payload[0];
                      return (
                        <div
                          className="bg-gray-900 px-3 py-2 rounded shadow-md"
                          style={{ minWidth: 120 }}
                        >
                          <div className="text-white text-sm font-medium mb-1">
                            {data.name}
                          </div>
                          <div className="text-white text-xs opacity-90">
                            {data.value} deportistas
                          </div>
                        </div>
                      );
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="mt-3 text-sm text-gray-600">
            Total deportistas:{" "}
            {deportistasByGrupos.reduce(
              (s, it) => s + (Number(it.value) || 0),
              0
            )}
          </div>
        </div>

        {/* Chart 4: Próximos eventos (Area chart counts by date) */}
        <div className={`rounded-xl border ${linesColor} p-4 bg-white`}>
          <div className="flex items-center justify-between mb-3">
            <div className={`${lettersTitlesColor} font-semibold`}>
              Próximos eventos
            </div>
            <Select
              placeholder="Tipo"
              style={{ width: 160 }}
              allowClear
              value={tipoEvento}
              onChange={(v) => setTipoEvento(v)}
            >
              <Select.Option value={null}>Todos</Select.Option>
              <Select.Option value="entrenamiento">Entrenamiento</Select.Option>
              <Select.Option value="torneo">Torneo</Select.Option>
            </Select>
          </div>
          <div className="w-full">
            {proximosLoading ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : (
              <div className="overflow-auto" style={{ maxHeight: 350 }}>
                <table className="min-w-full text-left divide-y divide-gray-100">
                  <thead>
                    <tr className="text-sm text-gray-500">
                      <th className="px-2 py-2">Fecha</th>
                      <th className="px-2 py-2">Evento</th>
                      <th className="px-2 py-2">Tipo</th>
                      <th className="px-2 py-2">Lugar</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm text-gray-700 divide-y">
                    {proximosData.length === 0 ? (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-2 py-4 text-center text-gray-500"
                        >
                          No hay próximos eventos
                        </td>
                      </tr>
                    ) : (
                      proximosData.slice(0, 6).map((ev) => (
                        <tr key={ev.id}>
                          <td className="px-2 py-3">{ev.date}</td>
                          <td className="px-2 py-3">{ev.name}</td>
                          <td className="px-2 py-3">{ev.tipo}</td>
                          <td className="px-2 py-3">{ev.lugar}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
